import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { decide, findCptCode, findIcd10Code, type StatementResponse } from '@emr/shared';

import { createPayment, fetchStatement } from '../api/rcm.ts';
import { useGrants } from '../auth/sessionContext.ts';
import { Button, ErrorText, Field, Panel } from '../components/primitives.tsx';
import { GatedRoute } from '../components/GatedRoute.tsx';
import { QuerySection } from '../components/QuerySection.tsx';

/**
 * A patient's RCM statement (`BACKLOG.md` R6): outstanding self-pay balance, the charges it is built
 * from (R1, a sibling module built in a separate isolated worktree), and this patient's payment
 * history, plus a form to record a new payment through the (stubbed today —
 * `docs/decisions/rcm-payments-pci-scope.md`) payment gateway.
 *
 * Not entitlement-gated — `capability="patient:read"` only, `feature={null}` — matching
 * `PatientChartPage.tsx`'s tabs, which are ordinary chart content, not a specialty module. Reachable
 * directly by URL, matching `PatientAestheticsPage.tsx`'s/`PatientDermatologyPage.tsx`'s own stated
 * reasoning: a route cannot rely on a nav link alone.
 */
export function PatientStatementPage() {
  const { t } = useTranslation('rcm');
  const { patientId } = useParams<{ patientId: string }>();

  if (patientId === undefined) {
    return <p className="m-0 text-sm text-danger">{t('common.noPatientId')}</p>;
  }

  return (
    <GatedRoute title={t('statement.pageTitle')} capability="patient:read" feature={null}>
      <StatementPanel patientId={patientId} />
    </GatedRoute>
  );
}

function dollars(cents: number): string {
  return (cents / 100).toFixed(2);
}

function BalanceHeading({ balanceCents }: { readonly balanceCents: number }) {
  const { t } = useTranslation('rcm');
  const owed = balanceCents > 0;
  const overpaid = balanceCents < 0;
  return (
    <p className="m-0 text-2xl font-semibold text-ink">
      {owed
        ? t('statement.balance.owed', { amount: dollars(balanceCents) })
        : overpaid
          ? t('statement.balance.overpaid', { amount: dollars(-balanceCents) })
          : t('statement.balance.paidInFull')}
    </p>
  );
}

function StatementPanel({ patientId }: { readonly patientId: string }) {
  const { t } = useTranslation('rcm');
  const grants = useGrants();
  const mayRecordPayment = decide(grants, 'billing:manage');
  const queryClient = useQueryClient();
  const queryKey = ['rcm', 'statement', patientId];

  const query = useQuery<StatementResponse>({
    queryKey,
    queryFn: ({ signal }) => fetchStatement(patientId, signal),
  });

  const [amountDollars, setAmountDollars] = useState('');
  const [method, setMethod] = useState('card');

  const recordPayment = useMutation({
    mutationFn: () =>
      createPayment(patientId, {
        encounterId: null,
        amountCents: Math.round(Number(amountDollars) * 100),
        method,
      }),
    onSuccess: () => {
      setAmountDollars('');
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  return (
    <QuerySection query={query} loadingLabel={t('common.loading')}>
      {(statement) => (
        <div className="flex flex-col gap-6">
          <Panel title={t('statement.balancePanelTitle')}>
            <BalanceHeading balanceCents={statement.balanceCents} />
          </Panel>

          <Panel title={t('statement.chargesPanelTitle')}>
            {statement.charges.length === 0 ? (
              <p className="m-0 text-sm text-ink-muted">{t('statement.chargesEmptyMessage')}</p>
            ) : (
              <ul className="m-0 flex list-none flex-col gap-2 p-0">
                {statement.charges.map((charge) => (
                  <li key={charge.id} className="rounded-control border border-line p-3 text-sm">
                    <p className="m-0 font-medium break-words whitespace-normal text-ink [overflow-wrap:anywhere]">
                      {charge.cptCode} —{' '}
                      {findCptCode(charge.cptCode)?.description ?? charge.cptCode}
                      {' — $'}
                      {dollars(charge.chargeAmountCents)}
                    </p>
                    <p className="m-0 break-words whitespace-normal text-ink-muted [overflow-wrap:anywhere]">
                      {charge.icd10Codes
                        .map((code) => `${code} (${findIcd10Code(code)?.description ?? code})`)
                        .join(', ')}
                    </p>
                    <p className="m-0 text-ink-muted">{charge.status}</p>
                  </li>
                ))}
              </ul>
            )}
          </Panel>

          <Panel title={t('statement.paymentsPanelTitle')}>
            {statement.payments.length === 0 ? (
              <p className="m-0 text-sm text-ink-muted">{t('statement.paymentsEmptyMessage')}</p>
            ) : (
              <ul className="m-0 flex list-none flex-col gap-2 p-0">
                {statement.payments.map((payment) => (
                  <li key={payment.id} className="rounded-control border border-line p-3 text-sm">
                    <p className="m-0 font-medium text-ink">
                      ${dollars(payment.amountCents)} — {payment.method}
                    </p>
                    <p className="m-0 text-ink-muted">
                      {payment.status}
                      {payment.processedAt !== null ? ` — ${payment.processedAt}` : ''}
                      {payment.gatewayRef?.startsWith('stub_') === true
                        ? ` — ${t('statement.recordPayment.simulatedTag')}`
                        : ''}
                    </p>
                  </li>
                ))}
              </ul>
            )}

            {mayRecordPayment ? (
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  if (amountDollars !== '') recordPayment.mutate();
                }}
                className="mt-4 flex flex-col gap-3 border-t border-row-line pt-4 sm:flex-row sm:flex-wrap sm:items-end"
              >
                <p className="m-0 basis-full text-xs text-ink-muted">
                  {t('statement.recordPayment.simulatedNotice')}
                </p>
                <Field
                  label={t('statement.recordPayment.amountLabel')}
                  value={amountDollars}
                  onChange={(event) => {
                    setAmountDollars(event.target.value);
                  }}
                  placeholder="0.00"
                  className="sm:max-w-[10ch]"
                />
                <label className="flex flex-col gap-1.5">
                  <span className="text-sm font-medium text-ink">
                    {t('statement.recordPayment.methodLabel')}
                  </span>
                  <select
                    value={method}
                    onChange={(event) => {
                      setMethod(event.target.value);
                    }}
                    className="rounded-control border border-field-line bg-field px-3 py-2 text-base text-ink sm:text-sm"
                  >
                    <option value="card">{t('statement.recordPayment.methodCard')}</option>
                    <option value="cash">{t('statement.recordPayment.methodCash')}</option>
                    <option value="check">{t('statement.recordPayment.methodCheck')}</option>
                  </select>
                </label>
                <Button type="submit" pending={recordPayment.isPending}>
                  {t('statement.recordPayment.submitButton')}
                </Button>
              </form>
            ) : null}
            {recordPayment.isError ? (
              <ErrorText message={t('statement.recordPayment.error')} />
            ) : null}
          </Panel>
        </div>
      )}
    </QuerySection>
  );
}
